import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { LoginService } from 'src/app/modules/shared/services/login/login.service';
import { ActivatedRoute, Router } from '@angular/router';
import { jwtDecode } from 'jwt-decode';
import { UserRolesService } from 'src/app/business-service/user-roles/user-roles.service';
import { ClientAdminService } from 'src/app/business-service/client-admin/client-admin.service';
import { GlobalService } from 'src/app/modules/shared/services/global/global.service';
import { ResponseNotificationService } from 'src/app/modules/shared/services/response-notification/response-notification.service';

@Component({
  selector: 'app-login',
  templateUrl: './login.component.html',
  styleUrls: ['./login.component.scss'],
})
export class LoginComponent implements OnInit {
  loginForm!: FormGroup;
  expired: boolean = false;
  invalidCreds: boolean = false;

  showSpinner: boolean = false;

  errorMessage: any;

  rememberMe: boolean = false;

  constructor(
    private _formBuilder: FormBuilder,
    private _loginService: LoginService,
    private _router: Router,
    private route: ActivatedRoute,
    private _userRoles: UserRolesService,
    private _globalService: GlobalService,
    private _clientAdmin: ClientAdminService,
    private _responseNotificationService: ResponseNotificationService
  ) { }

  ngOnInit(): void {
    this.loginForm = this._formBuilder.group({
      email: ['', [Validators.required, Validators.email]],
      password: ['', Validators.required]
    });

    this.expired = this.route.snapshot.queryParamMap.get("expired") ? true : false;
    this._loginService.logoutUser();

    // Saved used credentials
    const savedCredentials = localStorage.getItem('userCredentials');
    if (savedCredentials) {
      const { email, password } = JSON.parse(savedCredentials);
      this.loginForm.patchValue({ email, password });
      this.rememberMe = true;
    }

  }

  toggleRememberMe(event: any) {
    this.rememberMe = event.target.checked;
  }

  onSubmit(): void {
    if (this.loginForm.valid) {
      const formValues = this.serializeFormData(this.loginForm.value);
      this.showSpinner = true;
      this.saveUserCredInLocal(formValues);

      // Calling authentication api from login service
      this._loginService.loginRequest(formValues).subscribe(
        (data) => {
          if (data.access_token) {
            localStorage.setItem("Authorization", data.access_token);
            let userToken: any = jwtDecode(data.access_token);
            localStorage.setItem("CId", `${userToken.CId}`);
            if (userToken.CType) {
              localStorage.setItem("CType", `${userToken.CType}`);
            }
            localStorage.setItem("CDatabase", `${userToken.CDatabase}`);
            localStorage.setItem("CName", `${userToken.CName}`);
            localStorage.setItem("userName", `${userToken.UserName}`);
            localStorage.setItem("Role", `${userToken.Role}`);
            localStorage.setItem("aud", `${userToken.aud}`);
            localStorage.setItem("exp", `${userToken.exp}`);
            localStorage.setItem("iss", `${userToken.iss}`);
            localStorage.setItem("jti", `${userToken.jti}`);
            localStorage.setItem("sub", `${userToken.sub}`);
            localStorage.setItem("userId", `${userToken.UserId}`);

            this._userRoles.getPermissions(`${userToken.Role}`).subscribe(async (res) => {
              this._globalService.setRoles(res);
              this._globalService.setUserSenderAccess(res.userSenderAccess);
              if (!res.userSenderAccess) {
                if (await this._globalService.confirmActionPopup("Please verify your email to enable email-sending features associated with this account.", "Email Verification Required", "Verify Now")) {
                  const email = localStorage.getItem('sub') ?? "";
                  this._globalService.senderAccess(email);
                }
              }
              this._globalService.setOrganizationEmailSenderAccess(res.organizationSenderAccess);
              this._globalService.setOrganizationCommunicationEmailSenderAccess(res.organizationCommunicationEmails);
            })

            this._router.navigate(['']);
            this.showSpinner = false
          } else {
            this.invalidCreds = true;
            this.showSpinner = false
          }
        },
        (error) => {
          this.invalidCreds = true;
          this.errorMessage = error.error;
          this.showSpinner = false;
        }
      )
    }else{
      this._globalService.scrollToFirstInvalidControl();
      this.loginForm.markAllAsTouched();
      this._responseNotificationService.showMessage("Form is invalid, please enter all required information", true);
    }
  }

  saveUserCredInLocal(formData: any) {
    const { email, password } = formData;
    // Save credentials to localStorage if "Remember Me" is checked
    if (this.rememberMe) {
      localStorage.setItem(
        'userCredentials',
        JSON.stringify({ email, password })
      );
    } else {
      localStorage.removeItem('userCredentials');
    }
  }

  serializeFormData(formData: any) {
    const data = {
      email: formData.email?.toLowerCase(),
      password: formData.password
    }
    return data;
  }

  async forgotPassword() {
    // check if email is valid, only then proceed to forgot password
    if (this.loginForm.get('email')?.valid) {

      if (await this._globalService.confirmActionPopup("Are you sure you want to reset your password?", "Reset Password", "Yes")) {
        this._clientAdmin.forgotPassword(this.loginForm.get('email')?.value).subscribe({
          next: (res) => {
            if (res) {
              this._responseNotificationService.showMessage("Password reset email has been sent to your email address");
            } else {
              this._responseNotificationService.showMessage("Failed to send password reset email, please try again later", true);
            }
          },
          error: (err) => {
            console.error(err);
            this._responseNotificationService.showMessage("Failed to send password reset email, please try again later", true);
          }
        })
      }
    } else {
      this._responseNotificationService.showMessage("Please enter a valid email address", true);
    }
  }

}
